(() => {
  "use strict";

  /*
   * Script can be re-executed by the theme editor (section reload).
   * Only define everything once, afterwards just re-init sections.
   */
  if (window.InfinityScroll) {
    window.InfinityScroll.initAll();
    return;
  }

  const ROOT_MARGIN = "600px 0px";
  const TRIGGER_DISTANCE = 600; // px before the bottom to start loading

  /*
   * Unique key for a product card. Uses the product handle only, because
   * the URL differs by context (/products/x vs /collections/y/products/x).
   */
  const cardKey = (el) => {
    const link = el.querySelector('a[href*="/products/"]');
    if (!link) return null;

    const href = link.getAttribute("href") || "";
    const match = href.match(/\/products\/([^/?#]+)/);

    return match ? match[1] : href;
  };

  const init = (section) => {
    if (section.dataset.infinityReady === "true") return;

    const tabs = Array.from(
      section.querySelectorAll("[data-collection-handle]"),
    );
    const container = section.querySelector("[data-products-container]");
    const grid = section.querySelector("[data-product-grid]");
    const loader = section.querySelector("[data-loader]");
    const endMessage = section.querySelector("[data-end-message]");
    const sentinel = section.querySelector("[data-scroll-sentinel]");

    if (!container || !grid || !sentinel) return;

    section.dataset.infinityReady = "true";

    const ROWS = parseInt(section.dataset.rows, 10) || 3;
    const AJAX_SECTION =
      section.dataset.ajaxSection || "infinity-scroll-products";

    /* One state object per collection, so switching back is instant. */
    const states = new Map();

    let current = null; // active collection state
    let busy = false;
    let token = 0; // changes on every tab switch, cancels stale work
    let controller = new AbortController();

    /* ---------- helpers ---------- */

    const getColumns = () => {
      const cols = getComputedStyle(grid).gridTemplateColumns;
      const count = cols.split(" ").filter(Boolean).length;
      return count > 0 ? count : 4;
    };

    /* 3 rows = 3 x number of columns currently visible. */
    const batchSize = () => getColumns() * ROWS;

    const showLoader = () => loader && loader.classList.add("is-visible");
    const hideLoader = () => loader && loader.classList.remove("is-visible");

    const updateEnd = () => {
      if (!endMessage || !current) return;

      endMessage.hidden = !(
        current.done && current.shown >= current.nodes.length
      );
    };

    const notify = (initialLoad) => {
      document.dispatchEvent(
        new CustomEvent("infinity-scroll:loaded", {
          detail: {
            sectionId: section.dataset.sectionId,
            collectionUrl: container.dataset.collectionUrl || null,
            initialLoad: initialLoad,
          },
        }),
      );
    };

    /*
     * Distance-based check (does not depend on IntersectionObserver
     * changing state). True when the bottom of the product list is
     * within TRIGGER_DISTANCE of the bottom of the screen.
     */
    const nearBottom = () =>
      container.getBoundingClientRect().bottom - window.innerHeight <
      TRIGGER_DISTANCE;

    const maybeLoad = () => {
      if (!busy && current && nearBottom()) {
        loadMore();
      }
    };

    /* After rendering, check again once the layout has settled. */
    const recheck = () => {
      setTimeout(maybeLoad, 150);
    };

    /* ---------- state ---------- */

    const createState = (url) => ({
      url: url,
      nodes: [], // all product items fetched so far
      seen: new Set(), // product handles, avoids duplicates
      shown: 0, // how many are currently in the grid
      page: 0, // last server page fetched successfully
      done: false, // server has no more pages
    });

    /*
     * Request one page (24 products) of a collection from the
     * helper section: /collections/x?section_id=...&page=N
     */
    const fetchNextPage = async (state, signal) => {
      const page = state.page + 1;
      const url = new URL(state.url, window.location.origin);

      url.searchParams.set("section_id", AJAX_SECTION);
      url.searchParams.set("page", page);

      const response = await fetch(url.toString(), {
        headers: { "X-Requested-With": "XMLHttpRequest" },
        signal: signal,
      });

      if (!response.ok) {
        throw new Error(`Request failed (${response.status}): ${url}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const batch = doc.querySelector("[data-infinity-batch]");

      if (!batch) {
        throw new Error(
          `Section "${AJAX_SECTION}" was not returned. ` +
            `Check that sections/${AJAX_SECTION}.liquid exists in your theme.`,
        );
      }

      batch
        .querySelectorAll(":scope > .infinity-scroll__item")
        .forEach((item) => {
          const key = cardKey(item);

          if (key && state.seen.has(key)) return;
          if (key) state.seen.add(key);

          state.nodes.push(item);
        });

      state.page = page;
      state.done = batch.dataset.hasNext !== "true";
    };

    /* Make sure at least `needed` items are queued (or no more exist). */
    const ensure = async (state, needed, signal) => {
      while (state.nodes.length < needed && !state.done) {
        await fetchNextPage(state, signal);
      }
    };

    /* ---------- loading ---------- */

    const loadMore = async () => {
      if (busy || !current) return;

      const state = current;
      const myToken = token;
      const signal = controller.signal;

      if (state.done && state.shown >= state.nodes.length) {
        updateEnd();
        return;
      }

      busy = true;
      showLoader();

      let loaded = false;

      try {
        const isInitial = state.shown === 0;
        const size = batchSize();

        await ensure(state, state.shown + size, signal);

        if (myToken !== token) return; // tab was switched meanwhile

        const next = state.nodes.slice(state.shown, state.shown + size);

        next.forEach((node) => grid.appendChild(node));
        state.shown += next.length;

        loaded = true;
        updateEnd();
        notify(isInitial);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Infinity Scroll:", error);
        }
      } finally {
        if (myToken === token) {
          busy = false;
          hideLoader();

          if (loaded && !(state.done && state.shown >= state.nodes.length)) {
            recheck();
          }
        }
      }
    };

    /* ---------- tabs ---------- */

    const switchTab = (tab) => {
      const handle = tab.dataset.collectionHandle;
      const url = tab.dataset.collectionUrl;

      if (!handle || !url || tab.classList.contains("is-active")) return;

      /* Cancel anything in flight for the previous tab. */
      token += 1;
      controller.abort();
      controller = new AbortController();
      busy = false;
      hideLoader();

      tabs.forEach((item) => {
        const active = item === tab;

        item.classList.toggle("is-active", active);
        item.setAttribute("aria-selected", active ? "true" : "false");
      });

      container.dataset.currentCollection = handle;
      container.dataset.collectionUrl = url;

      if (!states.has(handle)) {
        states.set(handle, createState(url));
      }

      current = states.get(handle);

      /* Swap content (restores what was already loaded for this tab). */
      grid.replaceChildren();

      current.nodes.slice(0, current.shown).forEach((node) => {
        grid.appendChild(node);
      });

      /* If the tabs scrolled out of view, bring them back. */
      const top = tabs[0].parentElement.getBoundingClientRect().top;

      if (top < 0) {
        window.scrollTo({ top: window.scrollY + top - 20 });
      }

      updateEnd();

      if (current.shown === 0) {
        loadMore(); // first visit: load the first 3 rows
      } else {
        recheck();
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => switchTab(tab));
    });

    /* ---------- observer ---------- */

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: ROOT_MARGIN, threshold: 0 },
    );

    /* ---------- initial state (server-rendered first collection) ---------- */

    const firstTab = tabs.find((tab) => tab.classList.contains("is-active"));
    const firstHandle = container.dataset.currentCollection;

    const firstState = createState(
      container.dataset.collectionUrl ||
        (firstTab && firstTab.dataset.collectionUrl) ||
        "",
    );

    if (!firstState.url) {
      console.error(
        "Infinity Scroll: first collection URL is missing. " +
          "Check data-collection-url on [data-products-container] in the liquid file.",
      );
    }

    Array.from(
      grid.querySelectorAll(":scope > .infinity-scroll__item"),
    ).forEach((item) => {
      const key = cardKey(item);

      if (key) firstState.seen.add(key);
      firstState.nodes.push(item);
    });

    /*
     * Server rendered 12 products. Keep only the first N rows in the
     * grid (3 rows), the rest stay queued for the next load.
     */
    const initialBatch = batchSize();

    firstState.nodes.slice(initialBatch).forEach((node) => node.remove());
    firstState.shown = Math.min(initialBatch, firstState.nodes.length);

    states.set(firstHandle, firstState);
    current = firstState;

    observer.observe(sentinel);

    /*
     * Scroll listener (capture = also catches scrolling inside wrapper
     * elements, not only the window). Throttled with requestAnimationFrame.
     */
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;

      ticking = true;

      requestAnimationFrame(() => {
        ticking = false;
        maybeLoad();
      });
    };

    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", onScroll, { passive: true });

    /* In case the first rows don't fill the screen. */
    recheck();

    section.__infinityDestroy = () => {
      controller.abort();
      observer.disconnect();
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  };

  const initAll = () => {
    document.querySelectorAll(".infinity-scroll").forEach(init);
  };

  window.InfinityScroll = { initAll: initAll };

  initAll();

  /* Shopify theme editor support. */
  document.addEventListener("shopify:section:load", initAll);

  document.addEventListener("shopify:section:unload", (event) => {
    const el = event.target && event.target.querySelector(".infinity-scroll");

    if (el && el.__infinityDestroy) {
      el.__infinityDestroy();
    }
  });
})();
